"use client";

import { getBrowserSupabaseClient } from "@/lib/supabase/browser";
import { apiFetch } from "@/lib/api/client";

type Filter =
  | { type: "eq"; column: string; value: unknown }
  | { type: "neq"; column: string; value: unknown }
  | { type: "in"; column: string; value: unknown[] };

type OrderBy = {
  column: string;
  ascending: boolean;
};

type QueryPayload = {
  table: string;
  mode: "select" | "insert" | "update" | "upsert" | "delete";
  filters: Filter[];
  orderBy: OrderBy | null;
  limitBy: number | null;
  singleMode: "none" | "single" | "maybeSingle";
  selectClause: string | null;
  payload: Record<string, unknown> | Array<Record<string, unknown>> | null;
};

class RemoteQueryBuilder<T = any> implements PromiseLike<{ data: T; error: any }> {
  private readonly table: string;
  private filters: Filter[] = [];
  private orderBy: OrderBy | null = null;
  private limitBy: number | null = null;
  private singleMode: "none" | "single" | "maybeSingle" = "none";
  private mode: QueryPayload["mode"] = "select";
  private selectClause: string | null = "*";
  private payload: QueryPayload["payload"] = null;

  constructor(table: string) {
    this.table = table;
  }

  select(selectClause = "*") {
    if (this.mode === "select") {
      this.mode = "select";
    }
    this.selectClause = selectClause;
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push({ type: "eq", column, value });
    return this;
  }

  neq(column: string, value: unknown) {
    this.filters.push({ type: "neq", column, value });
    return this;
  }

  in(column: string, value: unknown[]) {
    this.filters.push({ type: "in", column, value });
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.orderBy = { column, ascending: options?.ascending !== false };
    return this;
  }

  limit(value: number) {
    this.limitBy = value;
    return this;
  }

  insert(payload: Record<string, unknown> | Array<Record<string, unknown>>) {
    this.mode = "insert";
    this.payload = payload;
    return this;
  }

  update(payload: Record<string, unknown>) {
    this.mode = "update";
    this.payload = payload;
    return this;
  }

  upsert(payload: Record<string, unknown> | Array<Record<string, unknown>>) {
    this.mode = "upsert";
    this.payload = payload;
    return this;
  }

  delete() {
    this.mode = "delete";
    this.payload = null;
    return this;
  }

  maybeSingle() {
    this.singleMode = "maybeSingle";
    return this;
  }

  single() {
    this.singleMode = "single";
    return this;
  }

  private async execute() {
    try {
      const result = await apiFetch<{ data: T }>("/api/db/query", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          table: this.table,
          mode: this.mode,
          filters: this.filters,
          orderBy: this.orderBy,
          limitBy: this.limitBy,
          singleMode: this.singleMode,
          selectClause: this.selectClause,
          payload: this.payload,
        } satisfies QueryPayload),
      });

      return { data: result.data, error: null };
    } catch (error) {
      return { data: null as T, error };
    }
  }

  then<TResult1 = { data: T; error: any }, TResult2 = never>(
    onfulfilled?:
      | ((value: { data: T; error: any }) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled ?? undefined, onrejected ?? undefined);
  }
}

function storageBucket(bucket: string) {
  return {
    async upload(path: string, file?: File, options?: Record<string, unknown>) {
      try {
        const formData = new FormData();
        formData.append("bucket", bucket);
        formData.append("path", path);
        if (file) {
          formData.append("file", file);
        }
        if (options?.contentType && typeof options.contentType === "string") {
          formData.append("contentType", options.contentType);
        }
        if (options?.cacheControl && typeof options.cacheControl === "string") {
          formData.append("cacheControl", options.cacheControl);
        }
        if (typeof options?.upsert === "boolean") {
          formData.append("upsert", String(options.upsert));
        }

        const data = await apiFetch<{ path: string; fullPath?: string }>("/api/storage/upload", {
          method: "POST",
          body: formData,
        });
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    getPublicUrl(path: string) {
      const browser = getBrowserSupabaseClient();
      return browser.storage.from(bucket).getPublicUrl(path);
    },
    async remove(paths: string[]) {
      try {
        const data = await apiFetch<{ removed: string[] }>("/api/storage/remove", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ bucket, paths }),
        });
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
  };
}

export const supabase = {
  auth: {
    getSession: (...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["getSession"]>) =>
      getBrowserSupabaseClient().auth.getSession(...args),
    onAuthStateChange: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["onAuthStateChange"]>
    ) => getBrowserSupabaseClient().auth.onAuthStateChange(...args),
    signOut: (...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["signOut"]>) =>
      getBrowserSupabaseClient().auth.signOut(...args),
    refreshSession: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["refreshSession"]>
    ) => getBrowserSupabaseClient().auth.refreshSession(...args),
    getUser: (...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["getUser"]>) =>
      getBrowserSupabaseClient().auth.getUser(...args),
    exchangeCodeForSession: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["exchangeCodeForSession"]>
    ) => getBrowserSupabaseClient().auth.exchangeCodeForSession(...args),
    getUserIdentities: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["getUserIdentities"]>
    ) => getBrowserSupabaseClient().auth.getUserIdentities(...args),
    updateUser: (...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["updateUser"]>) =>
      getBrowserSupabaseClient().auth.updateUser(...args),
    linkIdentity: (...args: any[]) => (getBrowserSupabaseClient().auth as any).linkIdentity(...args),
    unlinkIdentity: (...args: any[]) => (getBrowserSupabaseClient().auth as any).unlinkIdentity(...args),
    signInWithPassword: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["signInWithPassword"]>
    ) => getBrowserSupabaseClient().auth.signInWithPassword(...args),
    signUp: (...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["signUp"]>) =>
      getBrowserSupabaseClient().auth.signUp(...args),
    signInWithOAuth: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["signInWithOAuth"]>
    ) => getBrowserSupabaseClient().auth.signInWithOAuth(...args),
    resetPasswordForEmail: (
      ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["resetPasswordForEmail"]>
    ) => getBrowserSupabaseClient().auth.resetPasswordForEmail(...args),
    mfa: {
      getAuthenticatorAssuranceLevel: (
        ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["mfa"]["getAuthenticatorAssuranceLevel"]>
      ) => getBrowserSupabaseClient().auth.mfa.getAuthenticatorAssuranceLevel(...args),
      listFactors: (
        ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["mfa"]["listFactors"]>
      ) => getBrowserSupabaseClient().auth.mfa.listFactors(...args),
      enroll: (...args: any[]) => (getBrowserSupabaseClient().auth.mfa as any).enroll(...args),
      challengeAndVerify: (
        ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["mfa"]["challengeAndVerify"]>
      ) => getBrowserSupabaseClient().auth.mfa.challengeAndVerify(...args),
      unenroll: (
        ...args: Parameters<ReturnType<typeof getBrowserSupabaseClient>["auth"]["mfa"]["unenroll"]>
      ) => getBrowserSupabaseClient().auth.mfa.unenroll(...args),
    },
  },
  from(table: string) {
    return new RemoteQueryBuilder<any>(table);
  },
  storage: {
    from(bucket: string) {
      return storageBucket(bucket);
    },
  },
  functions: {
    async invoke<T = unknown>(name: string, options?: { body?: Record<string, unknown> }) {
      try {
        const data = await apiFetch<T>(`/api/actions/${name}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(options?.body ?? {}),
        });
        return { data, error: null };
      } catch (error) {
        return { data: null as T, error };
      }
    },
  },
  channel(name?: string) {
    return getBrowserSupabaseClient().channel(name ?? "default");
  },
  removeChannel(channel: Parameters<ReturnType<typeof getBrowserSupabaseClient>["removeChannel"]>[0]) {
    return getBrowserSupabaseClient().removeChannel(channel);
  },
};
